import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/common/Button";
import ErrorAlert from "../components/common/ErrorAlert";
import FormWrapper from "../components/common/FormWrapper";
import Input from "../components/common/Input";
import PasswordField from "../components/common/PasswordField";
import MathCaptcha from "../components/common/MathCaptcha";
import AuthShell from "../components/layout/AuthShell";
import { useMathCaptcha } from "../hooks/useMathCaptcha";
import authService from "../services/authService";
import {
  validateForgotPasswordEmail,
  validateVerifyOtp,
  validateResetPassword,
} from "../utils/validators";

const STEPS = {
  EMAIL: "EMAIL",
  OTP: "OTP",
  RESET: "RESET",
};

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const captcha = useMathCaptcha();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const handleEmailSubmit = async (e) => {
    if (e) e.preventDefault();

    // 1. Validate existing email field
    const validationErrors = validateForgotPasswordEmail(email);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // 2. Validate Math CAPTCHA
    if (!captcha.validateCaptcha()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");
      setInfoMessage("");
      await authService.forgotPassword(email);
      captcha.generateCaptcha();
      if (step === STEPS.EMAIL) {
        setStep(STEPS.OTP);
      } else {
        setInfoMessage("A new OTP has been sent to your email.");
      }
      setErrors({});
    } catch (error) {
      captcha.generateCaptcha();
      setSubmitError(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateVerifyOtp(otp);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await authService.verifyOtp(email, otp);
      setStep(STEPS.RESET);
      setErrors({});
    } catch (error) {
      setSubmitError(error.message || "Invalid OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateResetPassword(passwords);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await authService.resetPassword(email, otp, passwords.newPassword);
      setSuccessMessage("Success! Your password has been reset. Redirecting to login...");
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      setSubmitError(error.message || "Failed to reset password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderEmailStep = () => (
    <FormWrapper
      footer={
        <p className="text-sm text-[#4a5568] dark:text-[#b6bfd1]">
          Remember your password?{" "}
          <Link className="font-semibold text-[#b97a32] transition-colors hover:text-[#9f6829]" to="/login">
            Return to sign in
          </Link>
        </p>
      }
    >
      <form className="space-y-5" onSubmit={handleEmailSubmit} noValidate>
        <Input
          label="Business email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setErrors({});
            setSubmitError("");
          }}
          placeholder="operator@example.com"
          error={errors.email}
          autoComplete="email"
        />

        <MathCaptcha captcha={captcha} />

        {submitError ? <ErrorAlert message={submitError} /> : null}

        <Button
          type="submit"
          className="w-full bg-[#111c33] hover:bg-[#0b1324] focus:ring-[#111c33]"
          isLoading={isSubmitting}
        >
          Send OTP
        </Button>
      </form>
    </FormWrapper>
  );

  const renderOtpStep = () => (
    <FormWrapper
      footer={
        <p className="text-sm text-[#4a5568] dark:text-[#b6bfd1]">
          Didn't receive a code?{" "}
          <button
            type="button"
            className="font-semibold text-[#b97a32] transition-colors hover:text-[#9f6829]"
            onClick={handleEmailSubmit}
            disabled={isSubmitting}
          >
            Resend OTP
          </button>
        </p>
      }
    >
      <form className="space-y-5" onSubmit={handleOtpSubmit} noValidate>
        <Input
          label="Verification Code"
          name="otp"
          value={otp}
          onChange={(e) => {
            setOtp(e.target.value);
            setErrors({});
            setSubmitError("");
          }}
          placeholder="123456"
          error={errors.otp}
          maxLength={6}
        />

        {submitError ? <ErrorAlert message={submitError} /> : null}
        {infoMessage ? (
            <div className="rounded-lg bg-blue-50 p-4 text-sm font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                {infoMessage}
            </div>
        ) : null}

        <Button
          type="submit"
          className="w-full bg-[#111c33] hover:bg-[#0b1324] focus:ring-[#111c33]"
          isLoading={isSubmitting}
        >
          Verify OTP
        </Button>

        <div className="flex justify-center">
            <button
                type="button"
                className="text-xs font-bold text-[#7d8798] hover:text-[#b97a32]"
                onClick={() => setStep(STEPS.EMAIL)}
            >
                Back to email
            </button>
        </div>
      </form>
    </FormWrapper>
  );

  const renderResetStep = () => (
    <FormWrapper>
      <form className="space-y-5" onSubmit={handleResetSubmit} noValidate>
        <PasswordField
          label="New Password"
          name="newPassword"
          value={passwords.newPassword}
          onChange={(e) => {
            setPasswords((prev) => ({ ...prev, newPassword: e.target.value }));
            setErrors({});
            setSubmitError("");
          }}
          placeholder="Enter new password"
          error={errors.newPassword}
          hint="8-50 chars, including upper, lower, number, and symbol."
          autoComplete="new-password"
        />
        <PasswordField
          label="Confirm New Password"
          name="confirmPassword"
          value={passwords.confirmPassword}
          onChange={(e) => {
            setPasswords((prev) => ({ ...prev, confirmPassword: e.target.value }));
            setErrors({});
            setSubmitError("");
          }}
          placeholder="Confirm new password"
          error={errors.confirmPassword}
          autoComplete="new-password"
        />

        {submitError ? <ErrorAlert message={submitError} /> : null}
        {successMessage ? (
            <div className="rounded-lg bg-green-50 p-4 text-sm font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
                {successMessage}
            </div>
        ) : null}

        <Button
          type="submit"
          className="w-full bg-[#111c33] hover:bg-[#0b1324] focus:ring-[#111c33]"
          isLoading={isSubmitting}
          disabled={!!successMessage}
        >
          Reset Password
        </Button>
      </form>
    </FormWrapper>
  );

  return (
    <AuthShell>
      {step === STEPS.EMAIL && renderEmailStep()}
      {step === STEPS.OTP && renderOtpStep()}
      {step === STEPS.RESET && renderResetStep()}
    </AuthShell>
  );
}

export default ForgotPasswordPage;

